@if (state$ | async; as state) {
  <div class="page-header">
    <div class="page-title">Planowanie</div>
    <button class="btn" (click)="scrollToToday()">Dziś</button>
  </div>

  @if (state.loading) {
    Ładowanie…
  } @else {
    <div class="planner-scroll" #plannerScroll>
      <div class="planner-body" #plannerBody [style.width.px]="LABEL_WIDTH + state.dayCount * DAY_WIDTH">
        <div class="planner-bg" [style.left.px]="LABEL_WIDTH">
          @for (day of state.days; track day.offset) {
            @if (day.isWeekend) {
              <div class="weekend-stripe" [style.left.px]="day.offset * DAY_WIDTH" [style.width.px]="DAY_WIDTH"></div>
            }
          }
          <div class="today-line" [style.left.px]="state.todayOffset * DAY_WIDTH + DAY_WIDTH / 2"></div>
        </div>

        <div class="planner-row planner-months">
          <div class="planner-row-label"></div>
          <div class="planner-row-canvas" [style.width.px]="state.dayCount * DAY_WIDTH">
            @for (month of state.months; track month.label) {
              <div class="month-label" [style.left.px]="month.offset * DAY_WIDTH" [style.width.px]="month.span * DAY_WIDTH">{{ month.label }}</div>
            }
          </div>
        </div>

        <div class="planner-row planner-days">
          <div class="planner-row-label"></div>
          <div class="planner-row-canvas" [style.width.px]="state.dayCount * DAY_WIDTH">
            @for (day of state.days; track day.offset) {
              <div class="day-cell" [class.today]="day.isToday" [style.left.px]="day.offset * DAY_WIDTH" [style.width.px]="DAY_WIDTH">
                <div class="day-weekday">{{ day.weekday }}</div>
                <div class="day-number text-mono">{{ day.label }}</div>
              </div>
            }
          </div>
        </div>

        @for (row of state.rows; track row.orderId) {
          <div class="planner-row planner-order-row">
            <div class="planner-row-label">
              <div class="order-number">Zlec. {{ row.orderNumber }}</div>
              <div class="order-meta">
                <span>{{ row.name }}</span>
                @if (row.deadlineLabel) {
                  <span class="order-deadline" [class.overdue]="row.isDeadlineMissed"> · {{ row.deadlineLabel }}</span>
                }
              </div>
            </div>
            <div class="planner-row-canvas" [style.width.px]="state.dayCount * DAY_WIDTH">
              @if (row.deadlineOffset !== null) {
                <div class="deadline-marker" [class.overdue]="row.isDeadlineMissed" [style.left.px]="(row.deadlineOffset + 1) * DAY_WIDTH">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 16.5v-4.875" />
                  </svg>
                </div>
              }
              @for (bar of row.bars; track bar.orderId) {
                <div class="planner-bar"
                  [class.bar-at-risk]="bar.schedule === 'atRisk'"
                  [class.bar-behind]="bar.schedule === 'behind'"
                  [class.bar-done]="bar.schedule === 'done'"
                  [class.bar-preview]="bar.isPreview"
                  [style.left.px]="bar.startOffset * DAY_WIDTH"
                  [style.width.px]="bar.span * DAY_WIDTH"
                  (pointerdown)="onBarPointerDown($event, bar.orderId)"
                  (pointermove)="onPointerMove($event)"
                  (pointerup)="onPointerUp()"
                  (pointercancel)="onPointerCancel()">
                  <!-- Expected-progress baseline; the solid fill paints over it, so the visible stripes are exactly the lag -->
                  <div class="bar-expected" [style.width.%]="bar.expectedFraction * 100"></div>
                  <div class="bar-fill" [style.width.%]="bar.progressFraction * 100">
                    <!-- Second label copy, clipped to the fill — keeps the label readable on both fill and track -->
                    <span class="bar-label text-mono" [style.width.px]="bar.span * DAY_WIDTH - 2">{{ bar.rangeLabel }}</span>
                  </div>
                  <div class="bar-handle handle-start" (pointerdown)="onHandlePointerDown($event, bar.orderId, 'start')"></div>
                  <span class="bar-label text-mono">{{ bar.rangeLabel }}</span>
                  @if (!bar.isPreview) {
                    <button class="bar-unplan" title="Cofnij do niezaplanowanych" (pointerdown)="stop($event)" (click)="unplan($event, bar.orderId)">✕</button>
                  }
                  <div class="bar-handle handle-end" (pointerdown)="onHandlePointerDown($event, bar.orderId, 'end')"></div>
                </div>
              }
            </div>
          </div>
        }

        @if (state.rows.length === 0) {
          <div class="planner-empty">Brak zaplanowanych zleceń — przeciągnij zlecenie z listy poniżej na oś czasu.</div>
        }

        <!-- Absorbs leftover height so the sticky label column runs to the bottom -->
        <div class="planner-row planner-filler">
          <div class="planner-row-label"></div>
          <div class="planner-row-canvas" [style.width.px]="state.dayCount * DAY_WIDTH"></div>
        </div>
      </div>
    </div>

    <div class="planner-tray">
      <div class="tray-title">Niezaplanowane — przeciągnij na oś czasu</div>
      <div class="tray-chips">
        @for (chip of state.tray; track chip.orderId) {
          <div class="tray-chip"
            (pointerdown)="onTrayPointerDown($event, chip.orderId)"
            (pointermove)="onPointerMove($event)"
            (pointerup)="onPointerUp()"
            (pointercancel)="onPointerCancel()">
            <div class="tray-chip-order">Zlec. {{ chip.orderNumber }}</div>
            <div class="tray-chip-meta">
              <span>{{ chip.name }}</span>
              @if (chip.deadlineLabel) {
                <span class="text-mono"> · {{ chip.deadlineLabel }}</span>
              }
            </div>
          </div>
        } @empty {
          <div class="tray-empty">Wszystkie zlecenia są zaplanowane.</div>
        }
      </div>
    </div>
  }
}
